import { showView } from '../actions/publicActions';
import registerOnShowFullComposeOrchestrator from '../utils/registerOnShowFullComposeOrchestrator';
import { lazyInitializeCalendarsDataForEventDeeplink } from 'owa-calendar-bootstrap-utils';
import { showReadingPaneWithPerf } from 'owa-calendar-reading-pane-actions';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    BohemiaOptions,
} from 'owa-outlook-service-options';
import type ReadingPanePopoutViewState from 'owa-calendar-reading-pane-store/lib/store/schema/ReadingPanePopoutViewState';
import { ClientItemId, getUserMailboxInfo, MailboxInfo } from 'owa-client-ids';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getDataFromParentWindow, PopoutChildState, setPopoutChildState } from 'owa-popout-v2';
import {
    lazyInitializeProposeNewTimeForDeeplink,
    lazyInitializeForward,
} from 'owa-calendar-reading-pane-utils';

export interface CalendarReadingPaneRouteParameters {
    itemId?: string;
    account?: string;
}

export async function deepLinkReadingPaneRouteHandler(
    parameters: CalendarReadingPaneRouteParameters
) {
    // Initialize calendars
    await lazyInitializeCalendarsDataForEventDeeplink.importAndExecute();

    // Register so compose will show if the user clicks Edit or Duplicate
    registerOnShowFullComposeOrchestrator();

    if (parameters.itemId) {
        // In case item Id is rest id, convert to EwsId.
        // If itemId is EWSId, then convertRestIdToEwsId returns the same id without any changes
        const ewsId = convertRestIdToEwsId(parameters.itemId);

        let mailboxInfo: MailboxInfo = null;
        if (parameters.account) {
            mailboxInfo = getUserMailboxInfo(parameters.account);
        }

        const itemId: ClientItemId = {
            Id: ewsId,
            mailboxInfo: mailboxInfo,
        };

        const viewState: ReadingPanePopoutViewState = await getDataFromParentWindow(
            parseReadingPanePopoutViewState
        );

        if (viewState?.includeFluidOnPopout) {
            const options = getOptionsForFeature<BohemiaOptions>(OwsOptionsFeatureType.Bohemia);
            options.fluidEnabledForTenant = true;
        }

        const entrySource = viewState?.entrySource || 'Deeplink';
        const isProposeNewTime = !!viewState?.responseType;

        await showReadingPaneWithPerf(
            itemId,
            entrySource,
            false /* getSeriesMaster */,
            true /* isDeeplink */,
            false /* showModal */,
            true /* isInPopoutWindow */,
            viewState?.scrollToSection ?? undefined /* scrollToSectionOnInitialRender */,
            isProposeNewTime /* showSchedulingAssistant */
        );

        if (isProposeNewTime) {
            await lazyInitializeProposeNewTimeForDeeplink.importAndExecute(
                itemId,
                viewState.responseType,
                viewState.meetingMessageId,
                null /* editorTitleOverride */
            );
        }

        if (viewState?.isForward) {
            await lazyInitializeForward.importAndExecute(itemId, 'ReadForm' /* actionSource */);
        }
    }

    showView('Read', parameters.itemId, parameters.account);
    setPopoutChildState(PopoutChildState.Ready);
}

function parseReadingPanePopoutViewState(json: string): ReadingPanePopoutViewState {
    try {
        return JSON.parse(json) as ReadingPanePopoutViewState;
    } catch {
        return null;
    }
}
